import type { VisualEffect } from '../tilemap/types';

export class ExplosionEffect implements VisualEffect {
  private x: number;
  private y: number;
  private radius: number;
  private tileToScreenCenter: (x: number, y: number) => { x: number; y: number };
  private tileSize: number;

  private elapsed: number = 0;
  private duration: number = 500;
  private isFinished: boolean = false;

  constructor(options: {
    x: number;
    y: number;
    radius: number;
    tileToScreenCenter: (x: number, y: number) => { x: number; y: number };
    tileSize: number;
  }) {
    this.x = options.x;
    this.y = options.y;
    this.radius = options.radius;
    this.tileToScreenCenter = options.tileToScreenCenter;
    this.tileSize = options.tileSize;
  }

  public update(deltaSeconds: number): void {
    this.elapsed += deltaSeconds * 1000;
    if (this.elapsed >= this.duration) {
      this.isFinished = true;
    }
  }

  public renderOverlay(ctx: CanvasRenderingContext2D): void {
    if (this.isFinished) return;

    const progress = Math.min(this.elapsed / this.duration, 1.0);
    const center = this.tileToScreenCenter(this.x, this.y);

    // Max radius in pixels (adding 0.5 to cover the tile edges more nicely)
    const maxRadiusPx = (this.radius + 0.5) * this.tileSize;
    // Square root expansion for a "poof" feel
    const currentRadius = maxRadiusPx * Math.sqrt(progress);

    ctx.save();

    // Outer glow
    const glowGradient = ctx.createRadialGradient(
      center.x, center.y, 0,
      center.x, center.y, currentRadius * 1.5
    );
    glowGradient.addColorStop(0, `rgba(255, 100, 0, ${0.4 * (1 - progress)})`);
    glowGradient.addColorStop(1, 'rgba(255, 0, 0, 0)');
    ctx.fillStyle = glowGradient;
    ctx.beginPath();
    ctx.arc(center.x, center.y, currentRadius * 1.5, 0, Math.PI * 2);
    ctx.fill();

    // Main explosion core
    const coreGradient = ctx.createRadialGradient(
      center.x, center.y, 0,
      center.x, center.y, currentRadius
    );
    coreGradient.addColorStop(0, `rgba(255, 255, 200, ${1 - progress})`);
    coreGradient.addColorStop(0.2, `rgba(255, 255, 0, ${1 - progress})`);
    coreGradient.addColorStop(0.5, `rgba(255, 100, 0, ${0.8 * (1 - progress)})`);
    coreGradient.addColorStop(1, 'rgba(200, 0, 0, 0)');

    ctx.fillStyle = coreGradient;
    ctx.beginPath();
    ctx.arc(center.x, center.y, currentRadius, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  public isComplete(): boolean {
    return this.isFinished;
  }
}
